import React, { useState, useEffect } from 'react';
import { LuCheck, LuX } from 'react-icons/lu';
import {
  PhonicsLesson,
  PhonicsSound,
  useGetPhonicsSoundsQuery,
} from '@/api/types';
import { SymbolType, CdnObjectType } from '@/types';
import { assetCdnResourceExists } from '@/context/Ui';
import { findSoundsInString } from '@/utils/sounds';
import { Table } from '@/components';
import { ManagePanel } from './ManagePanel';
import {
  ACTIVITY_TYPE_READING_REVIEW_SOUNDS_IDENTIFIER,
  ACTIVITY_TYPE_READING_NEW_SOUNDS_IDENTIFIER,
  ACTIVITY_TYPE_READING_ORAL_BLENDING_IDENTIFIER,
  ACTIVITY_TYPE_READING_MANIPULATING_SOUNDS_IDENTIFIER,
  ACTIVITY_TYPE_READING_READ_WORDS_IDENTIFIER,
  ACTIVITY_TYPE_READING_READ_SENTENCES_IDENTIFIER,
  ACTIVITY_TYPE_SPELLING_REVIEW_SOUNDS_IDENTIFIER,
  ACTIVITY_TYPE_SPELLING_NEW_SOUNDS_IDENTIFIER,
  ACTIVITY_TYPE_SPELLING_FLICKING_FINGERS_IDENTIFIER,
  ACTIVITY_TYPE_SPELLING_WRITE_WORDS_IDENTIFIER,
  ACTIVITY_TYPE_SPELLING_WRITE_SENTENCES_IDENTIFIER,
} from '../LessonPlayer/types';

interface MediaAuditItemProps {
  symbol: string;
  symbolType: SymbolType;
  objectType: CdnObjectType;
  isExemplar: boolean;
}

const MediaAuditItem = ({
  symbol,
  symbolType,
  objectType,
  isExemplar = false,
}: MediaAuditItemProps) => {
  const [exists, setExists] = useState<boolean | undefined>();

  useEffect(() => {
    const checkExists = async () => {
      setExists(
        await assetCdnResourceExists(
          symbol,
          symbolType,
          objectType,
          isExemplar,
        ),
      );
    };

    checkExists();
  }, [symbol, symbolType, objectType, isExemplar]);

  if (exists === undefined) {
    return <>Loading</>;
  }

  return exists ? <LuCheck /> : <LuX />;
};

const getActivityLayout = (
  activity: PhonicsLesson['activities'][0],
  sounds: Array<PhonicsSound>,
) => {
  const foundSounds = activity.sounds
    ? findSoundsInString(
        sounds.map((sound) => sound.name) || [],
        activity.sounds,
      )
    : [];

  const foundWords = activity.words || [];
  const foundWords2 = activity.words2 || [];

  const foundSentences = activity.sentences || [];

  const foundSegmentations = activity.wordSegmentations || [];

  switch (activity.type.identifier) {
    case ACTIVITY_TYPE_READING_REVIEW_SOUNDS_IDENTIFIER:
    case ACTIVITY_TYPE_READING_NEW_SOUNDS_IDENTIFIER:
    case ACTIVITY_TYPE_SPELLING_REVIEW_SOUNDS_IDENTIFIER:
    case ACTIVITY_TYPE_SPELLING_NEW_SOUNDS_IDENTIFIER:
      return (
        <Table
          columns={{
            name: {
              label: 'Sound',
            },
            audio: {
              label: 'Audio',
              align: 'center',
            },
            exemplarAudio: {
              label: 'Exemplar audio',
              align: 'center',
            },
            image: {
              label: 'Image',
              align: 'center',
            },
          }}
          rows={foundSounds.map((sound) => ({
            name: sound,
            audio: (
              <MediaAuditItem
                symbol={sound}
                symbolType={SymbolType.Sound}
                objectType={CdnObjectType.Audio}
                isExemplar={false}
              />
            ),
            exemplarAudio: (
              <MediaAuditItem
                symbol={sound}
                symbolType={SymbolType.Sound}
                objectType={CdnObjectType.Audio}
                isExemplar={true}
              />
            ),
            image: (
              <MediaAuditItem
                symbol={sound}
                symbolType={SymbolType.Sound}
                objectType={CdnObjectType.Image}
                isExemplar={false}
              />
            ),
          }))}
          fillWidth={false}
        />
      );

    case ACTIVITY_TYPE_READING_ORAL_BLENDING_IDENTIFIER:
    case ACTIVITY_TYPE_READING_READ_WORDS_IDENTIFIER:
    case ACTIVITY_TYPE_SPELLING_WRITE_WORDS_IDENTIFIER:
      return (
        <Table
          columns={{
            name: {
              label: 'Word',
            },
            audio: {
              label: 'Audio',
              align: 'center',
            },
            image: {
              label: 'Image',
              align: 'center',
            },
          }}
          rows={foundWords.map((word) => ({
            name: word,
            audio: (
              <MediaAuditItem
                symbol={word}
                symbolType={SymbolType.Word}
                objectType={CdnObjectType.Audio}
                isExemplar={false}
              />
            ),
            image: (
              <MediaAuditItem
                symbol={word}
                symbolType={SymbolType.Word}
                objectType={CdnObjectType.Image}
                isExemplar={false}
              />
            ),
          }))}
          fillWidth={false}
        />
      );

    case ACTIVITY_TYPE_READING_MANIPULATING_SOUNDS_IDENTIFIER:
      return (
        <Table
          columns={{
            name: {
              label: 'Word',
            },
            audio: {
              label: 'Audio',
              align: 'center',
            },
            image: {
              label: 'Image',
              align: 'center',
            },
          }}
          rows={[
            ...foundWords.map((word) => ({
              name: word,
              audio: (
                <MediaAuditItem
                  symbol={word}
                  symbolType={SymbolType.Word}
                  objectType={CdnObjectType.Audio}
                  isExemplar={false}
                />
              ),
              image: (
                <MediaAuditItem
                  symbol={word}
                  symbolType={SymbolType.Word}
                  objectType={CdnObjectType.Image}
                  isExemplar={false}
                />
              ),
            })),
            ...foundWords2.map((word) => ({
              name: `${word} (2)`,
              audio: (
                <MediaAuditItem
                  symbol={word}
                  symbolType={SymbolType.Word}
                  objectType={CdnObjectType.Audio}
                  isExemplar={false}
                />
              ),
              image: (
                <MediaAuditItem
                  symbol={word}
                  symbolType={SymbolType.Word}
                  objectType={CdnObjectType.Image}
                  isExemplar={false}
                />
              ),
            })),
          ]}
          fillWidth={false}
        />
      );

    case ACTIVITY_TYPE_READING_READ_SENTENCES_IDENTIFIER:
    case ACTIVITY_TYPE_SPELLING_WRITE_SENTENCES_IDENTIFIER:
      return (
        <Table
          columns={{
            name: {
              label: 'Sentence',
            },
            audio: {
              label: 'Audio',
              align: 'center',
            },
            image: {
              label: 'Image',
              align: 'center',
            },
            parts: {
              label: 'Parts',
              align: 'center',
            },
          }}
          rows={foundSentences.map((sentence) => ({
            name: sentence,
            audio: (
              <MediaAuditItem
                symbol={sentence}
                symbolType={SymbolType.Sentence}
                objectType={CdnObjectType.Audio}
                isExemplar={false}
              />
            ),
            image: (
              <MediaAuditItem
                symbol={sentence}
                symbolType={SymbolType.Sentence}
                objectType={CdnObjectType.Image}
                isExemplar={false}
              />
            ),
            parts: (
              <Table
                columns={{
                  name: {
                    label: 'Part',
                  },
                  audio: {
                    label: 'Audio',
                    align: 'center',
                  },
                }}
                rows={sentence.split(' ').map((word) => ({
                  name: word,
                  audio: (
                    <MediaAuditItem
                      symbol={word}
                      symbolType={SymbolType.Word}
                      objectType={CdnObjectType.Audio}
                      isExemplar={false}
                    />
                  ),
                }))}
              />
            ),
          }))}
          fillWidth={false}
        />
      );

    case ACTIVITY_TYPE_SPELLING_FLICKING_FINGERS_IDENTIFIER:
      return (
        <Table
          columns={{
            name: {
              label: 'Segmentation',
            },
            audio: {
              label: 'Audio',
              align: 'center',
            },
            image: {
              label: 'Image',
              align: 'center',
            },
          }}
          rows={foundSegmentations.map((word) => ({
            name: word,
            audio: (
              <MediaAuditItem
                symbol={word.replaceAll('-', '')}
                symbolType={SymbolType.Word}
                objectType={CdnObjectType.Audio}
                isExemplar={false}
              />
            ),
            image: (
              <MediaAuditItem
                symbol={word.replaceAll('-', '')}
                symbolType={SymbolType.Word}
                objectType={CdnObjectType.Image}
                isExemplar={false}
              />
            ),
          }))}
          fillWidth={false}
        />
      );

    default:
      return 'Unknown activity type';
  }
};

interface ManageLessonMediaAuditProps {
  lesson: PhonicsLesson;
}

export const ManageLessonMediaAudit = ({
  lesson,
}: ManageLessonMediaAuditProps) => {
  const { data: soundsData } = useGetPhonicsSoundsQuery();

  return (
    soundsData?.getPhonicsSounds && (
      <ManagePanel title="Media Audit" yOrientation bgColorScheme="white">
        {lesson.activities.map((activity) => (
          <>
            <h3 className="text-xl">{activity.type.name}</h3>

            {getActivityLayout(activity, soundsData.getPhonicsSounds)}
          </>
        ))}
      </ManagePanel>
    )
  );
};
